// ---------------------------------------------------------------------------
// Resource library content.
//
// These are long form buyer education articles. They exist for two reasons:
//  1. Search. Fraud, risk and compliance teams research this problem before they
//     ever fill in a form, and we want to be the answer they find.
//  2. Sales. Every article doubles as something a champion can forward to a
//     sceptical stakeholder without needing us in the room.
//
// RULES FOR EDITING THIS FILE
//  1. No invented statistics. If a number is not something we can point at, use
//     the mechanism instead of the metric. Process detail sells better than a
//     figure a reviewer can challenge.
//  2. Regulations move. Anything citing a rule should describe what the rule
//     requires, not restate a deadline that may have changed.
//  3. Keep `updated` accurate. A stale date on a compliance article costs more
//     trust than it gains.
// ---------------------------------------------------------------------------

import { ARTICLE_SLUGS } from "./article-slugs";

/** Slugs are typed against the prerender list so the two cannot drift apart. */
export type ArticleSlug = (typeof ARTICLE_SLUGS)[number];

export interface ArticleSection {  /** Rendered as an h2 and used to build the on page table of contents. */
  heading: string;
  paragraphs: string[];
  /** Optional checklist or list of points shown under the paragraphs. */
  bullets?: string[];
  /** Optional highlighted aside. Use it for the single point worth remembering. */
  callout?: {
    title: string;
    body: string;
  };
}

export interface Article {
  slug: ArticleSlug;
  /** Short label used for filtering on the hub page. */
  category: string;
  /** Who this article is written for. Shown as a chip on the card. */
  audience: string;
  icon: string;
  title: string;
  /** Browser and search result title. Keep it under about 60 characters. */
  metaTitle: string;
  /** Search snippet. Keep it under about 160 characters. */
  metaDescription: string;
  /** Card summary on the hub page. Two sentences maximum. */
  excerpt: string;
  keywords: string;
  readMinutes: number;
  /** ISO date the article first went live. */
  published: string;
  /** ISO date of the last substantive edit. */
  updated: string;
  /** Pulled out above the article body so a skimmer gets value in ten seconds. */
  keyTakeaways: string[];
  sections: ArticleSection[];
  faqs: { question: string; answer: string }[];
  /** Slugs of related articles shown at the foot of the page. */
  related: ArticleSlug[];
}

export const ARTICLE_CATEGORIES = [
  "Fraud prevention",
  "Data quality",
  "Compliance",
  "Operations"
] as const;

export const ARTICLES: Article[] = [
  {
    slug: "deceased-identity-fraud-guide",
    category: "Fraud prevention",
    audience: "Fraud and risk leaders",
    icon: "shield",
    title: "Deceased identity fraud: how it works and where to stop it",
    metaTitle: "Deceased Identity Fraud: How It Works and How to Stop It",
    metaDescription:
      "How criminals use the identities of people who have died to open accounts and take jobs, why it slips past normal controls, and where to place the check.",
    excerpt:
      "Deceased identity fraud survives because the identity is real, the credit history is clean and nobody is left to dispute it. Here is the full mechanism and the four points in a customer lifecycle where you can actually break it.",
    keywords:
      "deceased identity fraud, ghosting fraud, dead person identity theft, synthetic identity fraud, deceased identity verification, identity fraud prevention banks",
    readMinutes: 9,
    published: "2026-01-14",
    updated: "2026-08-04",
    keyTakeaways: [
      "A deceased identity passes most fraud controls because every element of it is genuine",
      "The exposure window opens at the moment of death and closes only when your systems learn about it",
      "Origination screening is where the money is saved, but portfolio suppression is where it is recovered",
      "Speed of the death file matters more than the size of it"
    ],
    sections: [
      {
        heading: "Why a dead person is the perfect applicant",
        paragraphs: [
          "Fraud controls are built to find things that look wrong. A name that does not match an address. A date of birth invented to fit a credit thin file. A Social Security number that was never issued. A deceased identity has none of those problems. The name is real, the date of birth is real, the number was issued properly decades ago, and there is a long, well behaved credit history sitting behind it.",
          "Most of what fraud teams call identity verification is really consistency checking. Does this bundle of attributes hang together, and has it been seen before in a way that makes sense? A stolen deceased identity answers yes to both. It was a real person with a real footprint, right up until the moment they died.",
          "The industry calls this ghosting. It sits somewhere between straight identity theft and synthetic identity fraud. Unlike synthetic fraud, there is no fabricated element to detect. Unlike ordinary identity theft, there is nobody to notice a strange letter, check a statement, or call your fraud line. The victim cannot complain, and the family usually finds out months later, if at all."
        ],
        callout: {
          title: "The control that fails is the one you never built",
          body: "Almost every institution verifies that an identity exists. Far fewer verify that it is still attached to a living person. That gap is not a model tuning problem, it is a missing data input."
        }
      },
      {
        heading: "Where the identities come from",
        paragraphs: [
          "Obituaries are public. Funeral home notices, memorial pages and local news all publish full names, ages, home towns and surviving family. Combine that with a data broker record or an old breach dump and an operator has enough to reconstruct a usable identity within minutes.",
          "The second source is closer to home. Household members, carers and people with access to mail or filing cabinets already hold the missing pieces. A meaningful share of deceased identity abuse is committed by someone who knew the person, which is one reason it is rarely reported.",
          "The third source is professional. Identity packages are traded, and a recently deceased identity carries a premium because the buyer knows there is a window before it burns."
        ]
      },
      {
        heading: "The exposure window, and what actually closes it",
        paragraphs: [
          "The window opens on the date of death. It closes when your decisioning systems can see that the person has died. Everything in between is unpriced risk sitting inside a portfolio that looks healthy.",
          "That window is not one length. It is a chain, and each link adds days. A death is certified locally. It is registered with a state vital records office, often through an electronic registration system with varying levels of adoption. The state then reports onward. Downstream aggregators pick it up, normalise it, and publish it into a commercial file. Only then does it reach a screening decision at your end.",
          "This is why the useful question to ask a data provider is not how many records they hold. It is how many days pass between a death and that record being available to you, measured from the date of death rather than the date they loaded the file."
        ],
        bullets: [
          "Ask for time to availability measured from date of death, not from ingestion date",
          "Ask what proportion of records arrive within the first fourteen days",
          "Ask how state level coverage varies, because a nationwide claim can hide a weak state",
          "Ask what the provider does when a death is later corrected or rescinded"
        ]
      },
      {
        heading: "Four places to put the check",
        paragraphs: [
          "There is no single control point. The right answer is usually two or three of the following, sequenced so that the cheapest check happens first.",
          "Origination is where you prevent loss rather than chase it. A deceased check at application, before approval, converts a future charge off into a declined application. This is the highest value placement and the one most institutions add first.",
          "Portfolio monitoring is where you find what is already inside. A periodic sweep of the existing book against fresh death data surfaces accounts that were opened legitimately and later taken over, plus accounts where the customer died and activity continued.",
          "Servicing and payment events are the third point. A change of address, a new device, a payout request or an unusual withdrawal on an account belonging to someone who has died is a much stronger signal than the same event on a live account.",
          "Outreach suppression is the fourth. Before any collections, marketing or retention contact leaves your building, deceased records should be removed. This one is about complaint volume and reputational damage more than fraud loss, and it is the easiest to justify internally."
        ],
        callout: {
          title: "Sequence matters",
          body: "Screening at origination stops new exposure. Sweeping the portfolio recovers old exposure. Doing only the first leaves everything you already booked untouched."
        }
      },
      {
        heading: "What a good match looks like",
        paragraphs: [
          "A deceased match is a high consequence decision. Acting on a false positive means freezing a living customer's account, which is a service failure and, in some products, a regulatory one. Acting on a false negative means paying a fraudster.",
          "That means match quality has to be transparent. You need to see which elements agreed, how strongly, and what the confidence looks like, rather than being handed a yes or no. A match on name and date of birth alone is not the same as a match on name, date of birth and last known address.",
          "You also need date of death, not just a deceased flag. Date of death tells you whether an account was opened before or after the person died, and that single fact separates a legitimate estate matter from an outright fraud case."
        ],
        bullets: [
          "Confidence score on every match, not a binary flag",
          "Date of death returned so you can time bound the activity",
          "Last known address to support a second point of agreement",
          "Clear behavior when only a partial identity is submitted"
        ]
      },
      {
        heading: "Making the business case",
        paragraphs: [
          "The case is easier than most fraud investments because the cost model is simple. If you pay only for confirmed matches, the cost of running the check across a population is bounded by the number of deceased people actually in it, which is a small fraction of any healthy book.",
          "Frame the benefit in three buckets. Avoided credit loss on applications you would have approved. Recovered exposure on accounts already open. Removed complaint and reputational cost from contacting families of people who have died.",
          "The third bucket is often the one that gets the project funded, because it is the one your executive team hears about directly."
        ]
      }
    ],
    faqs: [
      {
        question: "How is deceased identity fraud different from synthetic identity fraud?",
        answer:
          "Synthetic identity fraud stitches together real and fabricated elements to create a person who never existed. Deceased identity fraud uses a person who genuinely existed, with a genuine history, who has died. Synthetic identities can often be caught by looking for a footprint that starts abruptly or does not hang together. A deceased identity has a complete, consistent history, so the only reliable way to catch it is to check whether the person is still alive."
      },
      {
        question: "Why do credit bureau death indicators miss cases?",
        answer:
          "Bureau death indicators depend on being reported, usually by a furnisher or a family member closing accounts. That reporting takes time and does not happen at all if nobody notices. A dedicated death data feed sourced from the record of death itself does not depend on a downstream party choosing to report it."
      },
      {
        question: "Should we screen at application or monitor the portfolio?",
        answer:
          "Both, but start with application screening if you have to pick one. Screening at origination prevents new exposure at the point where declining costs you nothing. Portfolio monitoring then finds the accounts already booked, which is recovery rather than prevention."
      }
    ],
    related: ["death-master-file-limitations", "deceased-suppression-workflow", "deceased-verification-pilot"]
  },

  {
    slug: "death-master-file-limitations",
    category: "Data quality",
    audience: "Data and fraud strategy teams",
    icon: "database",
    title: "Why the Death Master File is not enough on its own",
    metaTitle: "Death Master File Limitations: What the DMF Misses",
    metaDescription:
      "What the Social Security Death Master File covers, what it stopped covering, and why teams relying on it alone still approve applications for people who have died.",
    excerpt:
      "The DMF is a genuinely useful file with two structural gaps that most teams inherit without realising. Here is what it contains, what it stopped containing, and how to cover the difference.",
    keywords:
      "Death Master File, DMF, Limited Access Death Master File, SSA death data, death file coverage, deceased data source, death record freshness",
    readMinutes: 8,
    published: "2026-02-06",
    updated: "2026-08-04",
    keyTakeaways: [
      "The public DMF stopped including state reported deaths, which is where most death records originate",
      "Access to recent records is restricted, and recent records are exactly the ones fraud teams need",
      "A file can be nationwide and still be months behind on the deaths that matter",
      "Treat the DMF as one input, not as the definition of deceased"
    ],
    sections: [
      {
        heading: "What the Death Master File actually is",
        paragraphs: [
          "The Death Master File is a record of deaths reported to the Social Security Administration. It exists because the SSA needs to stop benefit payments, and it grew into a de facto industry reference because it was large, cheap and easy to obtain.",
          "It carries name, Social Security number, date of birth and date of death, which is enough to be genuinely useful. Many institutions built their deceased controls on it a decade or more ago and have not revisited that decision since.",
          "That is the problem. The file changed, and a lot of downstream controls did not."
        ]
      },
      {
        heading: "The two structural gaps",
        paragraphs: [
          "The first gap is source. Deaths reach the SSA from several places, including funeral homes, family members, financial institutions and state vital records offices. State reported deaths were removed from the version of the file made publicly available, which took a very large volume of records out of what most commercial users can see. If your provider is repackaging the public file, that volume is missing from your screening too.",
          "The second gap is time. Access to the most recent records is restricted to certified users with a legitimate fraud prevention purpose and appropriate safeguards, under a certification program with real requirements attached. Everyone else waits. The waiting period is measured in years, and the entire fraud exposure window sits inside it.",
          "Put those together and you get the outcome teams actually experience. The file confirms that someone who died in 2019 is deceased, which nobody was going to lend to anyway, and says nothing useful about someone who died last month, which is exactly the identity being used against you right now."
        ],
        callout: {
          title: "The test to run on your own data",
          body: "Take the deceased matches your current control produced last quarter. Plot them by date of death. If the distribution is heaviest at three years and older, your file is confirming history rather than preventing loss."
        }
      },
      {
        heading: "How death data reaches a commercial file",
        paragraphs: [
          "Understanding the chain explains why providers differ so much even when they all claim nationwide coverage.",
          "A death is certified by a medical certifier and reported by a funeral director. It is registered with the state vital records office, increasingly through an electronic death registration system, though adoption and processing speed are not uniform across states. The state maintains the authoritative record, and onward sharing is governed by state law and by agreements that vary considerably.",
          "Commercial providers assemble their file from whichever of those channels they can lawfully access, then match, normalise and deduplicate. Every hop adds latency, and every missing channel adds a gap. Two providers can both be honest about nationwide coverage while being weeks apart on the same death."
        ],
        bullets: [
          "Certification and funeral director reporting happens within days",
          "State registration follows, with speed varying by state and by system",
          "Onward availability depends on the channel and the agreement behind it",
          "Aggregation, matching and publication add the final stretch"
        ]
      },
      {
        heading: "Questions that separate providers",
        paragraphs: [
          "Coverage claims are almost useless as a comparison tool because everyone claims the same thing. These questions are not.",
          "Ask them in a proof of concept rather than in a questionnaire, because the answers only mean something when you can check them against your own book."
        ],
        bullets: [
          "How many days from date of death to the record being queryable by us, at the median and at the ninetieth percentile?",
          "How many new confirmed records do you add each week, and how does that compare to national mortality?",
          "Which states are slowest for you, and by how much?",
          "What is your process when a record is later corrected, and how do we learn about it?",
          "Do you return date of death and last known address, or only a deceased flag?",
          "What license or certification governs your access, and can we see it as part of vendor review?"
        ]
      },
      {
        heading: "What good looks like in practice",
        paragraphs: [
          "A workable deceased data input is fresh enough that the median record arrives within days rather than months, complete enough that it does not have a state sized hole in it, and specific enough to return a date of death and an address so you can adjudicate rather than guess.",
          "It should also be honest about what it is. A provider that tells you which states are slow and what their correction process looks like is a provider that has actually measured it.",
          "None of this means abandoning the DMF. It means treating it as one input into a deceased decision rather than as the answer, and covering the two gaps it structurally cannot cover."
        ]
      }
    ],
    faqs: [
      {
        question: "Is the Death Master File free?",
        answer:
          "There is a publicly available version and a restricted version. The publicly available version no longer includes state reported deaths and does not include the most recent records. Access to the restricted version requires certification, a legitimate fraud prevention or claims purpose, and demonstrated safeguards over the data."
      },
      {
        question: "How many deaths happen in the United States each year?",
        answer:
          "National mortality runs into the millions annually. The useful comparison is not the total, it is what proportion of that total your data provider actually surfaces to you within the first few weeks, because that early period is where your fraud exposure sits."
      },
      {
        question: "Can we just use obituary data instead?",
        answer:
          "Obituary data is fast but unstructured and incomplete. Not every death is published, published notices vary wildly in the identifying detail they contain, and matching an obituary to a customer record with confidence is difficult. It is a useful supplementary signal and a poor primary source."
      }
    ],
    related: ["deceased-identity-fraud-guide", "death-data-compliance-guide", "deceased-verification-pilot"]
  },

  {
    slug: "deceased-suppression-workflow",
    category: "Operations",
    audience: "Servicing, collections and operations",
    icon: "refresh",
    title: "Building a deceased suppression workflow that holds up",
    metaTitle: "Deceased Suppression Workflow for Servicing and Collections",
    metaDescription:
      "A practical operating model for suppressing deceased accounts before outreach, from match thresholds and hold states to estate handover and audit evidence.",
    excerpt:
      "Suppression fails in the handover, not in the match. This is the operating model that keeps confirmed deceased accounts out of outreach without freezing living customers by mistake.",
    keywords:
      "deceased suppression, collections deceased account, estate account handling, deceased customer process, servicing suppression workflow, deceased contact complaints",
    readMinutes: 8,
    published: "2026-03-11",
    updated: "2026-08-04",
    keyTakeaways: [
      "Suppression is an operating model, not a data feed",
      "Use two states, a soft hold and a confirmed hold, so you are never choosing between acting and waiting",
      "Every suppression needs an owner, a review clock and an exit path",
      "The evidence trail is what turns this into a defensible control"
    ],
    sections: [
      {
        heading: "The failure mode nobody plans for",
        paragraphs: [
          "Most teams buy death data, wire it into a nightly job, and produce a list. Then the list lands with an operations team that has no defined next action, no authority to close anything and no owner for the exceptions. Within a month the list is being ignored and the original problem is still live.",
          "The match is the easy part. What makes suppression work is deciding, in advance, exactly what happens to an account when a deceased signal arrives, who does it, how quickly, and how somebody gets un suppressed if the match was wrong."
        ]
      },
      {
        heading: "Two hold states, not one",
        paragraphs: [
          "A single deceased flag forces a bad choice. Set the bar high and you miss real cases. Set it low and you freeze living customers.",
          "Two states solve it. A soft hold triggers on a moderate confidence match and does one thing only: it stops outbound contact. No collections call, no marketing, no retention campaign. It does not restrict the customer, it does not change their balance, and it is invisible to them. The downside of a false positive is that somebody does not get a phone call.",
          "A confirmed hold triggers on a high confidence match or on a soft hold that has been validated by a human. This is the state that stops payments, restricts new credit and starts the estate process. It carries consequences, so it needs a higher bar and a name attached to the decision."
        ],
        callout: {
          title: "Why the soft hold earns its keep",
          body: "Contacting the family of someone who has died is the single most damaging thing an outreach operation can do. A soft hold removes that risk at a confidence level you would never accept for freezing an account."
        }
      },
      {
        heading: "The workflow, end to end",
        paragraphs: [
          "The shape below works for servicing, collections and retention with minor changes. Write it down before you turn on the feed."
        ],
        bullets: [
          "Match runs on a defined cadence against the live book, with a full sweep and a daily delta",
          "Moderate confidence match applies a soft hold and suppresses all outbound contact immediately",
          "High confidence match, or a validated soft hold, applies a confirmed hold and opens an estate case",
          "Every hold is assigned to a named owner within one business day",
          "Confirmed holds route to the estate or bereavement team with the date of death attached",
          "A review clock runs on every soft hold so nothing sits in limbo indefinitely",
          "A documented release path exists for false positives, with the reason recorded",
          "All state changes, the evidence behind them and the person who approved them are logged"
        ]
      },
      {
        heading: "Getting the estate handover right",
        paragraphs: [
          "The moment an account is confirmed deceased, the counterparty changes. You are no longer dealing with a customer, you are dealing with an estate, and often with a grieving family member who has been asked the same questions by four different departments.",
          "Two things make this materially better. First, carry the date of death through to the estate team so they are not asking the family for information you already have. Second, collapse the number of contact points. A single named handler beats a queue every time, and it is the difference between a complaint and a thank you letter.",
          "This is also where the commercial argument lives. Handled badly, a death in a customer household costs you the surviving partner's business too. Handled well, it is frequently the moment a family consolidates with you rather than away from you."
        ]
      },
      {
        heading: "Evidence, audit and the annual review",
        paragraphs: [
          "A suppression control that cannot be evidenced is a suppression control that does not exist as far as an examiner is concerned.",
          "Log the match itself, including which identity elements agreed and at what confidence. Log the state change and its timestamp. Log the human decision on any confirmed hold, with the approver. Log releases and the reason. That record is what lets you show a regulator, an auditor or your own board that the control operated, not just that it was purchased.",
          "Review the thresholds at least annually against outcomes. If soft holds are almost never escalating, your moderate threshold is too loose. If confirmed holds are producing releases, it is too tight. Both are fixable, but only if you kept the data."
        ],
        bullets: [
          "Match evidence retained with the matched elements and confidence",
          "Timestamped state transitions with the triggering rule",
          "Named approver on every confirmed hold and every release",
          "Threshold performance reviewed on a defined cadence"
        ]
      }
    ],
    faqs: [
      {
        question: "How often should we run the portfolio sweep?",
        answer:
          "Run a daily delta against newly published death records and a full portfolio sweep on a slower cadence, typically monthly or quarterly depending on book size. The daily delta catches the deaths that just happened, which is where the exposure is. The full sweep catches anything that was missed or that matched later because the record was corrected or enriched."
      },
      {
        question: "What confidence threshold should trigger suppression?",
        answer:
          "There is no universal number because it depends on the identity data quality in your own book. Start by running the match in observation mode for a few weeks, plot the confidence distribution against manual review outcomes, and set the soft hold threshold where the cost of a missed call is clearly lower than the cost of a wrong call. Set the confirmed hold threshold materially higher and require human validation below it."
      },
      {
        question: "Do we need to freeze the account as soon as we get a match?",
        answer:
          "No, and doing so on a moderate confidence match is usually the wrong call. Stopping outbound contact carries almost no downside if you are wrong. Freezing an account carries a significant one. Separate the two decisions and give them different thresholds."
      }
    ],
    related: ["deceased-identity-fraud-guide", "death-data-compliance-guide", "deceased-verification-pilot"]
  },

  {
    slug: "death-data-compliance-guide",
    category: "Compliance",
    audience: "Compliance and third party risk",
    icon: "gavel",
    title: "Death data and compliance: permissible use, GLBA and vendor review",
    metaTitle: "Death Data Compliance: Permissible Use, GLBA and Vendor Review",
    metaDescription:
      "What compliance and third party risk teams need to establish before using deceased data, from permissible purpose to Safeguards Rule obligations and vendor evidence.",
    excerpt:
      "Death data is powerful, lightly understood and easy to get wrong. This is the checklist compliance teams use to establish permissible use and to review a deceased data vendor properly.",
    keywords:
      "death data compliance, GLBA Safeguards Rule, permissible purpose deceased data, FCRA death indicator, vendor due diligence death data, 16 CFR Part 314",
    readMinutes: 9,
    published: "2026-04-08",
    updated: "2026-08-04",
    keyTakeaways: [
      "Establish your permissible purpose before you establish your data source",
      "The Safeguards Rule obligations attach to you, not only to your vendor",
      "How you use a deceased match can pull it into consumer reporting territory",
      "Vendor review should test the data chain, not just the security posture"
    ],
    sections: [
      {
        heading: "Start with purpose, not with product",
        paragraphs: [
          "The first question is not which provider to use. It is what you are going to do with the answer, because the use determines which rules apply.",
          "Using a deceased signal to prevent fraud at origination, to validate a claim, to stop a payment or to suppress outreach is a straightforward operational use. Using it as a factor in a decision about a living consumer's eligibility, terms or employment is a different thing entirely and may bring consumer reporting obligations into scope.",
          "Write the intended use down, in plain language, before procurement starts. Most of the compliance friction later in these projects comes from a use case that was never defined precisely enough for legal to give a clean answer."
        ],
        callout: {
          title: "One sentence worth drafting early",
          body: "We will use confirmed deceased records to do X, at point Y in the lifecycle, and the decision the match drives is Z. If you cannot fill that in, you are not ready to buy."
        }
      },
      {
        heading: "The Safeguards Rule obligations you keep",
        paragraphs: [
          "Financial institutions in scope of the FTC Safeguards Rule at 16 CFR Part 314 carry a set of obligations that do not transfer to a vendor. Bringing in a deceased data provider adds a service provider to your program, it does not reduce your program.",
          "In practice, adding this vendor should touch several parts of your existing information security program. Your risk assessment should reflect the new data flow. Your service provider oversight should cover the new provider, with written security obligations and a reassessment cadence. Your access controls should cover who inside your organization can see match results, because a deceased match is sensitive information about a real household.",
          "None of this is unusual, and a provider that has done it before will hand you the mapping rather than making you build it."
        ],
        bullets: [
          "Update the written risk assessment to cover the new data flow",
          "Record the provider in your service provider inventory with written security obligations",
          "Define who internally can view and export match results",
          "Confirm retention and deletion for both the records you send and the results you receive",
          "Include the provider in your incident response contact chain"
        ]
      },
      {
        heading: "When a deceased match becomes a consumer report",
        paragraphs: [
          "This is the area teams most often get wrong, and it is worth a short conversation with counsel rather than a long one after the fact.",
          "A deceased person is not a consumer for most purposes, so a confirmed match on someone who has genuinely died sits outside a lot of consumer protection machinery. The risk is not there. The risk is in the false positive, because a false positive is a statement about a living person.",
          "If a match, or a near match, contributes to a decision about a living individual's credit, insurance, employment or eligibility, you need to be confident about which framework applies and what rights that individual has. This is another argument for the two state model described in our suppression guide: suppressing an outbound call is not an eligibility decision, whereas declining an application is."
        ]
      },
      {
        heading: "Reviewing the vendor properly",
        paragraphs: [
          "Standard security questionnaires cover encryption, access control and incident response well. They cover data provenance badly, and provenance is where the risk in this category actually lives.",
          "Ask where the data comes from, under what license or certification, and what happens to that access if the underlying agreement lapses. Ask what the provider does with the records you submit, because sending your customer file to a vendor who enriches their own product with it is a materially different arrangement to one who deletes it after delivery.",
          "Then ask for the evidence rather than the assertion."
        ],
        bullets: [
          "Named source channels and the license or certification governing access",
          "Written confirmation that your submitted records are not resold, relicensed or used to enrich other products",
          "Documented retention and deletion schedule for inputs and outputs, with earlier deletion available on request",
          "Encryption in transit and at rest, with key management described",
          "Subprocessor list with locations, and notification of changes",
          "Incident response commitments including notification timing",
          "Current attestation status stated honestly, including anything still in progress",
          "Control mapping to the framework your examiners use"
        ]
      },
      {
        heading: "Documenting it so it survives an examination",
        paragraphs: [
          "The artefacts that make this easy later are boring and quick to produce at the time. A one page description of the data flow. The permissible purpose statement. The vendor assessment and its date. The retention schedule. The threshold and escalation rules for acting on a match. The log design that evidences the control operating.",
          "Assemble those during implementation and the annual review becomes a half day exercise. Assemble them afterwards and it becomes a project."
        ]
      }
    ],
    faqs: [
      {
        question: "Does GLBA apply to deceased individuals?",
        answer:
          "The protections generally attach to consumers, and there is a reasonable argument that a deceased person falls outside much of that. Two practical points remain. First, the records you submit to run the check include living people, so the data flow itself is in scope. Second, a false positive is a statement about a living person. Treat the whole process as in scope and you avoid the argument entirely."
      },
      {
        question: "Do we need a consumer reporting agency relationship to use death data?",
        answer:
          "It depends entirely on how the output is used. Operational uses such as suppressing outbound contact or validating a claim are generally different from using the information as a factor in an eligibility decision about a living consumer. Define the use case precisely and take a legal view on it before you go live."
      },
      {
        question: "What should we ask for in a vendor security package?",
        answer:
          "A completed questionnaire, a control mapping to the framework your examiners use, encryption and key management detail, the retention and deletion schedule, incident response and notification commitments, a business continuity summary, a named subprocessor list, the most recent penetration test summary, and an honest statement of attestation status including anything still in progress."
      }
    ],
    related: ["deceased-suppression-workflow", "death-master-file-limitations", "deceased-verification-pilot"]
  },

  {
    slug: "deceased-verification-pilot",
    category: "Operations",
    audience: "Program owners and analytics",
    icon: "chart",
    title: "How to run a deceased verification pilot in 30 days",
    metaTitle: "How to Run a Deceased Verification Pilot in 30 Days",
    metaDescription:
      "A week by week plan to prove or disprove a deceased data provider against your own book, with the metrics that make the result defensible internally.",
    excerpt:
      "You do not need a six month evaluation to know whether a death file works. You need one clean sample, four measurements and a control group. Here is the thirty day version.",
    keywords:
      "deceased verification pilot, death data proof of concept, evaluate death data provider, deceased match rate benchmark, fraud data pilot plan",
    readMinutes: 7,
    published: "2026-05-13",
    updated: "2026-08-04",
    keyTakeaways: [
      "Test against your own historical book, where you already know the answers",
      "Measure time to availability from date of death, because that is the metric that predicts prevented loss",
      "Run a known deceased control set so you can measure recall, not just volume",
      "Decide the go or no go thresholds before you see any results"
    ],
    sections: [
      {
        heading: "Set the question before you set the schedule",
        paragraphs: [
          "A pilot that asks whether the data is good will produce an argument. A pilot that asks four specific numerical questions will produce a decision.",
          "Write these down first, agree them with whoever signs the contract, and set the pass thresholds while you are still neutral."
        ],
        bullets: [
          "What proportion of our known deceased population does the file find? That is recall.",
          "Of the matches it returns, what proportion survive manual review? That is precision.",
          "How many days after date of death did each record become available to us?",
          "How many matches would have been actionable at the decision point we care about, rather than after it?"
        ]
      },
      {
        heading: "Week one, build the sample",
        paragraphs: [
          "Pull a historical population where the outcome is already known. Twelve to twenty four months of applications or accounts is usually enough, and history is essential because you need to be able to check the answer.",
          "Then build a control set. Take accounts you already know were deceased, ideally confirmed through an estate process or a family notification, and hold them aside. Without a control set you can measure how much the file returns but not how much it misses, and the miss rate is the number that matters.",
          "Agree the file format and the minimum identity elements at the same time. Name and date of birth is workable. Adding last known address materially improves match confidence and reduces the review burden."
        ],
        callout: {
          title: "Do not skip the control set",
          body: "A provider returning a large number of matches looks impressive. A provider returning a large proportion of your known deceased population is the one that is actually working."
        }
      },
      {
        heading: "Week two, run it and instrument it",
        paragraphs: [
          "Submit the sample, capture the raw results and keep everything. You want the confidence score on every match, the matched elements, the date of death and the last known address.",
          "Then compute the availability metric properly. For each match, take the date of death and compare it to the date the record became queryable. Report the median and the ninetieth percentile rather than an average, because averages hide the tail and the tail is what determines whether the file prevents anything.",
          "If you are evaluating two providers, run the identical sample through both and compare on the same four questions. Different samples produce different numbers and no useful comparison."
        ]
      },
      {
        heading: "Week three, adjudicate",
        paragraphs: [
          "Take a random sample of the matches and review them properly against whatever internal evidence you have. This is where precision gets measured, and it is worth doing by hand rather than by rule.",
          "While you are in there, split the results by confidence band. You are looking for the band where precision is high enough to act automatically and the band below it where a human should look. Those two numbers become your production thresholds, so the pilot pays for itself twice.",
          "Also check the control set. Every known deceased account that the file did not return is worth understanding. Sometimes it is an identity data quality problem on your side, which is useful to know regardless."
        ]
      },
      {
        heading: "Week four, write the decision",
        paragraphs: [
          "The output is a short document, not a slide deck. State the four numbers, compare them to the thresholds set in week one, and recommend go or no go.",
          "Then quantify. Take the matches that were available before your decision point, multiply by the average exposure on that product, and you have the avoided loss figure. Take the matches on accounts already in outreach and you have the complaint and reputational figure. Those two lines are what a budget holder needs.",
          "If it is a go, the production design is mostly already written. You have thresholds from week three, a file format from week one, and a measured latency profile that tells you what cadence to run at."
        ],
        bullets: [
          "Recall, precision, median and ninetieth percentile availability, actionable match rate",
          "Comparison to the thresholds agreed before results were seen",
          "Avoided loss estimate and outreach complaint estimate",
          "Recommended production thresholds and run cadence"
        ]
      },
      {
        heading: "Keeping the pilot cheap",
        paragraphs: [
          "The reason this fits in a month is that a per record model with no charge for non matches makes the sample size question disappear. You are not paying to test a hundred thousand records against a file, you are paying only for the small number that come back confirmed.",
          "That removes the usual procurement bottleneck, which is committing to a minimum before you have any evidence. Test at real volume, pay for what is actually found, and let the numbers decide."
        ]
      }
    ],
    faqs: [
      {
        question: "How large should the pilot sample be?",
        answer:
          "Large enough that the expected number of deceased matches is comfortably into the hundreds, so precision and recall are measurable rather than anecdotal. For most consumer books that means tens of thousands of records over a twelve to twenty four month window. Use history rather than live traffic so you can verify the answers."
      },
      {
        question: "What is a good deceased match rate?",
        answer:
          "There is no single benchmark, because it depends on the age profile of your book and the age of the records in it. That is exactly why the control set matters. Recall against known deceased accounts is comparable across providers and across time. Raw match volume is not."
      },
      {
        question: "Can we pilot without sending real customer data?",
        answer:
          "You can start with a synthetic or masked sample to validate the integration and the file format, but the four metrics that decide the question need real identities matched against real records. Run the security review first, then use real data under the agreed controls."
      }
    ],
    related: ["death-master-file-limitations", "deceased-identity-fraud-guide", "death-data-compliance-guide"]
  },

  {
    slug: "deceased-ssn-misuse-in-hiring",
    category: "Fraud prevention",
    audience: "HR, screening and corporate security",
    icon: "briefcase",
    title: "Deceased identities in hiring, and why screening misses them",
    metaTitle: "Deceased Identity Use in Hiring and Employment Screening",
    metaDescription:
      "How deceased identities pass employment screening and onboarding, what the exposure looks like for an employer, and where to add the check.",
    excerpt:
      "A deceased identity clears most background checks because everything in it is real. For employers the exposure is not just a bad hire, it is payroll, access and liability attached to a person who does not exist.",
    keywords:
      "deceased identity hiring, employment screening deceased, background check death record, onboarding identity fraud, payroll fraud deceased identity",
    readMinutes: 6,
    published: "2026-06-17",
    updated: "2026-08-04",
    keyTakeaways: [
      "Background screening confirms that an identity exists and has a clean record, not that it is alive",
      "Remote onboarding removed most of the informal checks that used to catch this",
      "The exposure spans payroll, system access, physical access and liability",
      "The check belongs at offer stage, before credentials are issued"
    ],
    sections: [
      {
        heading: "What a background check actually confirms",
        paragraphs: [
          "A standard pre employment screen confirms that a name, date of birth and Social Security number belong together, checks criminal records across relevant jurisdictions, and verifies employment and education history. Every one of those checks passes cleanly for a deceased identity, because the identity is genuine and the history is genuine.",
          "Right to work verification has the same characteristic. It confirms the documents presented are consistent and valid on their face. It is not designed to confirm that the person standing in front of you is the person the documents describe, and in a remote process there is nobody standing in front of you at all.",
          "So the screen comes back clear, the offer goes out, and a fraudulent worker is inside the perimeter with a badge, a laptop and a payroll record."
        ]
      },
      {
        heading: "What changed with remote hiring",
        paragraphs: [
          "Distributed hiring removed the incidental controls that used to catch this quietly. Somebody used to hand over a passport in person. A local reference happened to know the family. A first day involved walking into a building.",
          "In a fully remote process the identity is a set of documents and a video call, and both are reproducible. The volume of applications also went up, which pushes screening toward automation, which pushes it toward exactly the checks a deceased identity passes."
        ],
        callout: {
          title: "The signal that is missing",
          body: "Employment screening is very good at answering whether this person has done anything wrong. It is not built to answer whether this person is still alive, because until recently that was not a question anyone needed to ask."
        }
      },
      {
        heading: "The exposure for an employer",
        paragraphs: [
          "The obvious cost is payroll paid to somebody who is not who they claim to be. That is rarely the largest cost.",
          "The larger exposure is access. A hired identity carries system credentials, customer data access, financial system permissions and often physical access. In regulated industries it may also carry a licensed or registered role that was granted on the strength of a fraudulent identity, which becomes a supervisory problem as well as a security one.",
          "There is a third layer. Contractors and vendor staff frequently receive access with lighter screening than employees. If the deceased identity check is only applied to direct hires, the gap simply moves."
        ],
        bullets: [
          "Payroll and benefits paid against a fraudulent identity",
          "System and data access granted to an unverifiable person",
          "Physical access credentials issued to a fabricated worker",
          "Licensed or registered roles held under a stolen identity",
          "Contractor and vendor populations screened to a lower standard"
        ]
      },
      {
        heading: "Where to put the check",
        paragraphs: [
          "At offer stage, alongside the existing background screen and before any credential is issued. This is the point where the cost of stopping is lowest and the cost of proceeding starts to climb sharply.",
          "Run it against the same identity elements the screen already collects, so there is no additional data capture and no additional candidate friction. A deceased result at this point is not a hiring decision about a living candidate, it is a signal that the identity presented does not belong to the person presenting it, and it should route to security rather than to the hiring manager.",
          "Then run a periodic sweep across the active workforce and the contractor population. Somebody hired legitimately three years ago is a different exposure to somebody hired last week, and a sweep is the only thing that finds an identity that was substituted after onboarding."
        ]
      },
      {
        heading: "Handling a match without creating a problem",
        paragraphs: [
          "This needs a defined path, because handled clumsily a false positive is a serious matter for a real candidate.",
          "Route matches to a security or investigations function, not to the hiring manager. Confirm through a second identity element before any action. Do not communicate a suspected deceased match to the candidate as an eligibility decision, and do not treat it as an adverse action question without taking a view on which framework applies to the way you are using it.",
          "The volume here is genuinely low, which means the review cost is genuinely low. That is the argument for setting the threshold conservatively and having a human look at everything before anything happens."
        ]
      }
    ],
    faqs: [
      {
        question: "Will a standard background check catch a deceased identity?",
        answer:
          "Usually not. Criminal, employment and education checks all pass cleanly because the identity is real and the history is real. Some screening providers include a death indicator, but it typically depends on lagging sources, so it confirms older deaths rather than the recent ones being actively used."
      },
      {
        question: "Should we screen contractors as well as employees?",
        answer:
          "Yes. Contractor and vendor staff frequently receive comparable system and physical access with lighter identity screening, which makes that population the path of least resistance once direct hiring is covered."
      },
      {
        question: "Where in the process should the check run?",
        answer:
          "At offer stage, before any credential is issued, using the identity elements already collected for the background screen. Add a periodic sweep across the active workforce to catch identities substituted after onboarding."
      }
    ],
    related: ["deceased-identity-fraud-guide", "deceased-verification-pilot", "death-data-compliance-guide"]
  }
];

/** Newest first, which is the order the hub page renders. */
export const ARTICLES_BY_DATE: Article[] = [...ARTICLES].sort((a, b) =>
  b.published.localeCompare(a.published)
);

export function findArticle(slug: ArticleSlug): Article | undefined {
  return ARTICLES.find(article => article.slug === slug);
}

export function relatedArticles(article: Article): Article[] {
  return article.related
    .map(slug => findArticle(slug))
    .filter((item): item is Article => Boolean(item));
}
